import Phaser from 'phaser';

const SKY_TOP = 0x1e1b4b;
const SKY_MID = 0x7c3aed;
const SKY_BOT = 0xfbbf24;

const MOUNTAIN_DARK = 0x1c1917;
const MOUNTAIN_RIM = 0x713f12;
const MOUNTAIN_GLOW = 0xfbbf24;

const CITY_BODY = 0x0f172a;
const CITY_GOLD = 0xf59e0b;
const CITY_WINDOW = 0xfde68a;
const ACCENT_GOLD = 0xfbbf24;
const STAR = 0xffffff;

export function drawWakandaSkyBackground(
  g: Phaser.GameObjects.Graphics,
  w: number,
  h: number,
) {
  g.clear();
  const step = Math.max(1, Math.floor(h / 40));
  for (let y = 0; y < h; y += step) {
    const t = y / h;
    let r: number, gC: number, b: number;
    if (t < 0.45) {
      const u = t / 0.45;
      r = lerp((SKY_TOP >> 16) & 0xff, (SKY_MID >> 16) & 0xff, u);
      gC = lerp((SKY_TOP >> 8) & 0xff, (SKY_MID >> 8) & 0xff, u);
      b = lerp(SKY_TOP & 0xff, SKY_MID & 0xff, u);
    } else {
      const u = (t - 0.45) / 0.55;
      r = lerp((SKY_MID >> 16) & 0xff, (SKY_BOT >> 16) & 0xff, u);
      gC = lerp((SKY_MID >> 8) & 0xff, (SKY_BOT >> 8) & 0xff, u);
      b = lerp(SKY_MID & 0xff, SKY_BOT & 0xff, u);
    }
    g.fillStyle((r << 16) | (gC << 8) | b, 1);
    g.fillRect(0, y, w, step + 1);
  }

  const rand = mulberry32(909);
  g.fillStyle(STAR, 0.8);
  const starCount = Math.max(6, Math.floor(w / 90));
  for (let i = 0; i < starCount; i++) {
    const sx = rand() * w;
    const sy = rand() * h * 0.35;
    const sr = rand() < 0.2 ? 1.5 : 1;
    g.fillCircle(sx, sy, sr);
  }
}

export function drawWakandaSilhouette(
  g: Phaser.GameObjects.Graphics,
  w: number,
  h: number,
  seed = 404,
) {
  const rand = mulberry32(seed);
  const baselineY = Math.floor(h * 0.82);

  drawMountBashenga(g, w, h, baselineY);
  drawGoldenCity(g, w, baselineY, rand);

  g.fillStyle(ACCENT_GOLD, 0.6);
  for (let i = 0; i < Math.floor(w / 40); i++) {
    const dx = rand() * w;
    const dy = baselineY - 2 + rand() * 6;
    g.fillRect(dx, dy, 2, 1);
  }
}

function drawMountBashenga(
  g: Phaser.GameObjects.Graphics,
  w: number,
  _h: number,
  baselineY: number,
) {
  const cx = w / 2;
  const peakY = Math.floor(baselineY * 0.35);
  const baseW = Math.min(w * 0.55, 520);

  g.fillStyle(MOUNTAIN_DARK, 1);
  g.beginPath();
  g.moveTo(cx - baseW / 2, baselineY);
  g.lineTo(cx - baseW * 0.12, peakY + 20);
  g.lineTo(cx, peakY);
  g.lineTo(cx + baseW * 0.12, peakY + 20);
  g.lineTo(cx + baseW / 2, baselineY);
  g.closePath();
  g.fillPath();

  g.fillStyle(MOUNTAIN_RIM, 0.9);
  g.lineStyle(2, MOUNTAIN_RIM, 1);
  g.beginPath();
  g.moveTo(cx - baseW * 0.12, peakY + 20);
  g.lineTo(cx, peakY);
  g.lineTo(cx + baseW * 0.12, peakY + 20);
  g.strokePath();

  const earOffset = baseW * 0.08;
  const earH = 16;
  g.fillStyle(MOUNTAIN_DARK, 1);
  g.beginPath();
  g.moveTo(cx - earOffset - 8, peakY + 4);
  g.lineTo(cx - earOffset - 2, peakY - earH);
  g.lineTo(cx - earOffset + 4, peakY + 4);
  g.closePath();
  g.fillPath();
  g.beginPath();
  g.moveTo(cx + earOffset - 4, peakY + 4);
  g.lineTo(cx + earOffset + 2, peakY - earH);
  g.lineTo(cx + earOffset + 8, peakY + 4);
  g.closePath();
  g.fillPath();

  g.fillStyle(MOUNTAIN_GLOW, 0.8);
  g.fillCircle(cx - baseW * 0.03, peakY + 6, 2);
  g.fillCircle(cx + baseW * 0.03, peakY + 6, 2);
}

function drawGoldenCity(
  g: Phaser.GameObjects.Graphics,
  w: number,
  baselineY: number,
  rand: () => number,
) {
  const cityTopY = Math.floor(baselineY * 0.45);
  const maxBuildingH = baselineY - cityTopY;

  g.fillStyle(CITY_BODY, 1);
  g.fillRect(0, baselineY, w, 200);

  const buildings: { x: number; w: number; h: number }[] = [];
  let x = -20;
  while (x < w + 20) {
    const bw = 44 + Math.floor(rand() * 50);
    const bh = Math.floor(maxBuildingH * (0.45 + rand() * 0.9));
    buildings.push({ x, w: bw, h: bh });
    x += bw + Math.floor(rand() * 8) - 6;
  }

  for (const b of buildings) {
    const bx = b.x;
    const by = baselineY - b.h;
    g.fillStyle(CITY_BODY, 1);
    g.fillRect(bx, by, b.w, b.h + 4);

    g.fillStyle(CITY_GOLD, 1);
    g.fillRect(bx, by, b.w, 2);
    g.fillRect(bx, by + b.h - 2, b.w, 2);

    const stripeCount = 2 + Math.floor(rand() * 2);
    for (let s = 0; s < stripeCount; s++) {
      const sx = bx + Math.floor(b.w * ((s + 1) / (stripeCount + 1)));
      g.fillRect(sx, by + 4, 2, b.h - 8);
    }

    const rows = Math.max(2, Math.floor(b.h / 14));
    const cols = Math.max(2, Math.floor(b.w / 10));
    for (let r = 0; r < rows; r++) {
      for (let c = 0; c < cols; c++) {
        if (rand() > 0.5) continue;
        const wx = bx + 3 + c * Math.floor(b.w / cols);
        const wy = by + 6 + r * 14;
        if (wy > baselineY - 4) continue;
        g.fillStyle(rand() < 0.3 ? ACCENT_GOLD : CITY_WINDOW, 0.9);
        g.fillRect(wx, wy, 3, 4);
      }
    }

    if (b.h > maxBuildingH * 0.7 && rand() < 0.4) {
      g.fillStyle(CITY_GOLD, 1);
      g.fillRect(bx + Math.floor(b.w / 2) - 1, by - 12, 2, 12);
      g.fillCircle(bx + Math.floor(b.w / 2), by - 14, 2);
    }
  }
}

function lerp(a: number, b: number, t: number): number {
  return Math.round(a + (b - a) * t);
}

function mulberry32(seed: number) {
  let a = seed >>> 0;
  return () => {
    a = (a + 0x6d2b79f5) >>> 0;
    let t = a;
    t = Math.imul(t ^ (t >>> 15), t | 1);
    t ^= t + Math.imul(t ^ (t >>> 7), t | 61);
    return ((t ^ (t >>> 14)) >>> 0) / 4294967296;
  };
}
